/**
 * Copies → what the preview views draw: the mini copy (from value stats, before a run), the
 * exact copy's widgets (from a slice's tiles), step 1's audience, split field, value rows and tile
 * plan. A copy's colours come from the theme's tone (light or dark, from the presentation-themes
 * catalog) plus the brand accent, which is data.
 */
import { formatRowCount, pluralize } from "./distributionFormat";
import type { PresentationThemeOption } from "../presentationThemes/presentationThemes.types";
import { paletteAccent } from "./distributionThemeLookMappers";
import {
  DEFAULT_AUDIENCE,
  type AudienceApi,
  type DistributionContentApi,
  type DistributionThemeApi,
  type CopyTileApi,
  type LeftOutTileApi,
  type RecipientBrandApi,
  type SplitCheckApi,
  type SplitFieldApi,
  type TileKindApi,
  type ValueKpiApi,
  type ValueStatApi,
} from "./distributions.types";
import type { CopyKpiView, CopyPreviewModel, CopyWidget, TilePlanView, ValueRowView } from "./distributionView.types";

const KIND_LABEL: Record<TileKindApi, [string, string]> = {
  kpi: ["KPI", "KPIs"],
  chart: ["chart", "charts"],
  table: ["table", "tables"],
  note: ["note", "notes"],
  link: ["link", "links"],
  bi_view: ["embedded BI view", "embedded BI views"],
  still: ["captured image", "captured images"],
};

const KIND_NAME: Record<TileKindApi, string> = {
  kpi: "KPI", chart: "Chart", table: "Table", note: "Note", link: "Link", bi_view: "Embedded BI view", still: "Captured image",
};

/** The saved audience; content saved before the choice existed reads as `outside`. Null without content. */
export const audienceOf = (content: Pick<DistributionContentApi, "audience"> | null | undefined): AudienceApi | null =>
  content ? content.audience ?? DEFAULT_AUDIENCE : null;

/** A listed field as a write sends it: magic-core's `SplitField` refuses the list-only keys. */
export const toSplitField = ({ field, model_release_id, label, description, source_ref }: SplitFieldApi): SplitFieldApi => ({
  field,
  model_release_id,
  label,
  description,
  source_ref,
});

/** "Region = West", shortened past 16 characters as the copy's filter chip does. */
export const filterExpression = (fieldLabel: string, value: string, maxLength = Infinity): string =>
  `${fieldLabel} = ${value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value}`;

export const toCopyKpi = (kpi: ValueKpiApi): CopyKpiView => ({
  label: kpi.label,
  value: kpi.masked ? null : kpi.formatted,
  delta: kpi.masked ? null : kpi.delta_formatted,
  direction: kpi.masked ? null : kpi.direction,
  masked: kpi.masked,
  placeholder: kpi.masked ? "Masked" : kpi.formatted === null ? "Left out" : null,
});

export interface CopyPreviewInput {
  storyTitle: string;
  fieldLabel: string;
  value: string;
  brand: RecipientBrandApi;
  stat: ValueStatApi | null;
  splitCheck: SplitCheckApi | null;
  /** Left-out tiles the owner sends as they are. */
  sendAsIs: readonly string[];
  tone?: "light" | "dark";
  /** Used when the brand has no accent. */
  fallbackAccent: string;
}

/** The mini copy: brand band, filter chip, up to three KPIs, the trend bars and the tile counts. */
export const toCopyPreviewModel = (input: CopyPreviewInput): CopyPreviewModel => {
  const kpis = (input.stat?.kpis ?? []).slice(0, 3).map(toCopyKpi);
  const personalized = input.splitCheck?.personalized.length ?? 0;
  const leftOut = (input.splitCheck?.left_out.length ?? 0) - input.sendAsIs.length;
  const more = personalized - kpis.length - 1;
  return {
    brand: {
      name: input.brand.name ?? "",
      mark: (input.brand.mark ?? input.brand.name?.[0] ?? "?").slice(0, 2),
      accent: input.brand.accent ?? input.fallbackAccent,
      logoUrl: input.brand.logo_url,
    },
    filterLabel: filterExpression(input.fieldLabel, input.value, 16),
    title: input.storyTitle,
    subtitle: `${input.value} only`,
    kpis,
    bars: input.stat?.trend ?? [],
    moreTilesLabel: input.splitCheck && more > 0 ? `+ ${pluralize(more, "more tile", "more tiles")}` : null,
    leftOutLabel: input.splitCheck ? `${leftOut} left out${input.sendAsIs.length ? ` · ${input.sendAsIs.length} as is` : ""}` : null,
    tone: input.tone ?? "light",
  };
};

const cellText = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/** A slice's tiles → widgets in story order; masked values become "Masked". */
export const toCopyWidgets = (tiles: readonly CopyTileApi[]): CopyWidget[] =>
  [...tiles]
    .sort((a, b) => a.position - b.position)
    .map((tile): CopyWidget => {
      switch (tile.kind) {
        case "kpi":
          return {
            kind: "kpi", id: tile.item_id, title: tile.title, width: tile.width, comparison: tile.comparison_label,
            kpi: toCopyKpi({ label: tile.title, formatted: tile.formatted, delta_formatted: tile.delta_formatted, direction: tile.direction, masked: tile.masked }),
          };
        case "chart": {
          const [labelKey, valueKey] = Object.keys(tile.rows[0] ?? {});
          return {
            kind: "chart", id: tile.item_id, title: tile.title, width: tile.width, caption: tile.caption, chartType: tile.chart_type,
            spec: tile.vega_spec, rows: tile.rows, maskedColumns: tile.masked_columns,
            bars: tile.rows.map((row) => ({ label: cellText(row[labelKey]), value: Number(row[valueKey]) || 0 })),
          };
        }
        case "table":
          return {
            kind: "table", id: tile.item_id, title: tile.title, width: tile.width, columns: tile.columns,
            rows: tile.rows.map((row) => tile.columns.map((col) => (col.masked ? "Masked" : cellText(row[col.name])))),
          };
        case "note":
          return { kind: "note", id: tile.item_id, width: tile.width, text: tile.text };
        default:
          return { kind: "link", id: tile.item_id, width: tile.width, title: tile.title, url: tile.url };
      }
    });

/**
 * Step 1's value list rows: "1.42M rows · <first KPI> <figure>" and "<Field> = <value>". The figure
 * is the first KPI the value stats report unmasked, named by its own label: never a fixed name.
 */
export const toValueRows = (fieldLabel: string, values: readonly string[], stats: readonly ValueStatApi[]): ValueRowView[] =>
  values.map((value) => {
    const stat = stats.find((s) => s.value === value);
    const empty = stat?.rows === 0;
    const kpi = stat?.kpis.find((k) => !k.masked && k.formatted);
    let statsLabel = "";
    if (empty) statsLabel = "No data · skipped";
    else if (stat) statsLabel = [stat.rows !== null ? `${formatRowCount(stat.rows)} rows` : null, kpi ? `${kpi.label} ${kpi.formatted}` : null].filter(Boolean).join(" · ");
    return { value, statsLabel, expression: filterExpression(fieldLabel, value), empty };
  });

const kindsLabel = (kinds: readonly TileKindApi[]): string => {
  const counts = new Map<TileKindApi, number>();
  kinds.forEach((k) => counts.set(k, (counts.get(k) ?? 0) + 1));
  return Array.from(counts).map(([kind, n]) => `${n} ${n === 1 ? KIND_LABEL[kind][0] : KIND_LABEL[kind][1]}`).join(", ");
};

/** "What each copy includes": personalize count, sensitive note and the left-out list. */
export const toTilePlanView = (check: SplitCheckApi, fieldLabel: string, sendAsIs: readonly string[]): TilePlanView => {
  const sensitive = check.personalized.filter((t) => t.sensitive).map((t) => t.title);
  const out: LeftOutTileApi[] = check.left_out;
  const leftOutCount = out.filter((t) => !sendAsIs.includes(t.item_id)).length;
  return {
    headline: `${check.personalized.length} of ${check.total_tiles} tiles personalize to each ${fieldLabel}`,
    kindsLabel: kindsLabel(check.personalized.map((t) => t.kind)),
    sensitiveNames: sensitive,
    sensitiveNote: sensitive.length ? `${sensitive.join(", ")} ${sensitive.length === 1 ? "is" : "are"} marked sensitive. Sensitive values are masked in every copy.` : null,
    leftOut: out.map((t) => ({
      id: t.item_id,
      title: t.title,
      kind: t.kind,
      kindLabel: KIND_NAME[t.kind],
      reason: sendAsIs.includes(t.item_id) ? "Included as is. Everyone sees the same content." : t.message,
      canOptIn: t.can_opt_in,
      included: sendAsIs.includes(t.item_id),
      masked: t.reason === "masked",
    })),
    leftOutHeadline: `${pluralize(leftOutCount, "tile is", "tiles are")} left out of every copy`,
  };
};

/**
 * The distribution themes as dropdown options. A theme's `id` (the catalog key) is the option key,
 * so whatever the user picks is sent back unchanged as `theme_id`. The first theme is the default.
 * The swatch is the theme's own accent from its palette (none when the catalog sends no palette).
 */
export const toDistributionThemeCatalog = (
  items: readonly DistributionThemeApi[],
): { options: PresentationThemeOption[]; defaultThemeKey: string | undefined } => ({
  options: items.map((theme) => ({
    key: theme.id,
    label: theme.name,
    tone: theme.tone,
    description: theme.description,
    swatch: paletteAccent(theme.palette) ?? undefined,
  })),
  defaultThemeKey: items[0]?.id,
});
